import React from 'react'
import {
    Box,
    LinearProgress,
    Tooltip,
    makeStyles,
    Theme,
} from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
    },
    bar: {
        height: 8,
        borderRadius: 4,
        width: '100%',
    },
    barCompact: {
        height: 6,
        borderRadius: 3,
    },
    tick: {
        position: 'absolute',
        top: 0,
        transform: 'translateX(-1px)',
        pointerEvents: 'none',
        zIndex: 1,
    },
    tickDefault: {
        height: 8,
    },
    tickCompact: {
        height: 6,
    },
    tickInteractive: {
        pointerEvents: 'auto',
        width: 8,
        marginLeft: -3,
    },
    paceTick: {
        borderLeft: '2px dashed #f59e0b',
    },
    paceTickSlash: {
        borderLeft: '3px solid #f59e0b',
    },
    lastYearTick: {
        borderLeft: '2px dashed #475569',
    },
    paceLabel: {
        color: '#d97706',
        fontSize: '0.625rem',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    lastYearLabel: {
        color: '#475569',
        fontSize: '0.625rem',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    tickSlash: {
        height: 16,
        top: -5,
    },
    paceCaptionOn: {
        color: '#047857',
        fontSize: '0.85rem',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    paceCaptionBehind: {
        color: '#b91c1c',
        fontSize: '0.85rem',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
}))

interface MarkerConfig {
    key: string
    percent: number
    label: string
    variant: 'pace' | 'lastYear'
    labelRow: 0 | 1
}

interface SeasonLeaseUpBarProps {
    percentLeased: number
    expectedPercent?: number | null
    lastYearPercent?: number | null
    compact?: boolean
    hideMarkerLabels?: boolean
    paceCaption?: string | null
    paceOn?: boolean
}

function clampPercent(value: number): number {
    return Math.min(100, Math.max(0, value))
}

function labelTransform(percent: number): string {
    if (percent <= 8) return 'translateX(0)'
    if (percent >= 92) return 'translateX(-100%)'
    return 'translateX(-50%)'
}

function buildMarkers(
    expectedPercent: number | null | undefined,
    lastYearPercent: number | null | undefined,
): MarkerConfig[] {
    const markers: MarkerConfig[] = []

    if (expectedPercent != null) {
        markers.push({
            key: 'pace',
            percent: clampPercent(expectedPercent),
            label: `pace ${expectedPercent.toFixed(0)}%`,
            variant: 'pace',
            labelRow: 0,
        })
    }

    if (lastYearPercent != null) {
        const lastYear = clampPercent(lastYearPercent)
        const paceMarker = markers[0]
        const tooClose =
            paceMarker != null && Math.abs(paceMarker.percent - lastYear) < 12

        markers.push({
            key: 'last-year',
            percent: lastYear,
            label: `last year ${lastYearPercent.toFixed(0)}%`,
            variant: 'lastYear',
            labelRow: tooClose ? 1 : 0,
        })
    }

    return markers
}

export const SeasonLeaseUpBar: React.FC<SeasonLeaseUpBarProps> = ({
    percentLeased,
    expectedPercent,
    lastYearPercent,
    compact = false,
    hideMarkerLabels = false,
    paceCaption = null,
    paceOn,
}) => {
    const classes = useStyles()
    const markers = buildMarkers(expectedPercent, lastYearPercent)
    const paceMarker = markers.find((marker) => marker.variant === 'pace')
    const showDefaultLabels =
        markers.length > 0 && !hideMarkerLabels && !paceCaption
    const showPaceCaption = Boolean(paceCaption && paceMarker)

    return (
        <Box className={classes.root}>
            <Box position="relative" width="100%">
                {markers.map((marker) => {
                    const isPace = marker.variant === 'pace'
                    const tick = (
                        <Box
                            className={`${classes.tick} ${
                                compact
                                    ? classes.tickCompact
                                    : classes.tickDefault
                            } ${
                                isPace
                                    ? paceCaption
                                        ? classes.paceTickSlash
                                        : classes.paceTick
                                    : classes.lastYearTick
                            } ${
                                hideMarkerLabels ? classes.tickInteractive : ''
                            } ${
                                isPace && paceCaption ? classes.tickSlash : ''
                            }`}
                            style={{ left: `${marker.percent}%` }}
                        />
                    )

                    if (!hideMarkerLabels || (isPace && paceCaption)) {
                        return (
                            <React.Fragment key={`tick-${marker.key}`}>
                                {tick}
                            </React.Fragment>
                        )
                    }

                    return (
                        <Tooltip
                            key={`tick-${marker.key}`}
                            title={marker.label}
                            placement="top"
                        >
                            {tick}
                        </Tooltip>
                    )
                })}

                <LinearProgress
                    variant="determinate"
                    value={clampPercent(percentLeased)}
                    className={compact ? classes.barCompact : classes.bar}
                />
            </Box>

            {showDefaultLabels && (
                <Box
                    position="relative"
                    width="100%"
                    mt={compact ? 0.5 : 0.75}
                    minHeight={markers.some((m) => m.labelRow === 1) ? 28 : 14}
                >
                    {markers.map((marker) => (
                        <Box
                            key={`label-${marker.key}`}
                            position="absolute"
                            top={marker.labelRow === 1 ? 14 : 0}
                            left={`${marker.percent}%`}
                            style={{
                                transform: labelTransform(marker.percent),
                            }}
                        >
                            <span
                                className={
                                    marker.variant === 'pace'
                                        ? classes.paceLabel
                                        : classes.lastYearLabel
                                }
                            >
                                {marker.label}
                            </span>
                        </Box>
                    ))}
                </Box>
            )}

            {showPaceCaption && paceMarker && (
                <Box position="relative" width="100%" mt={0.5} minHeight={18}>
                    <Box
                        position="absolute"
                        top={0}
                        left={`${paceMarker.percent}%`}
                        style={{
                            transform: labelTransform(paceMarker.percent),
                        }}
                    >
                        <span
                            className={
                                paceOn
                                    ? classes.paceCaptionOn
                                    : classes.paceCaptionBehind
                            }
                        >
                            {paceCaption}
                        </span>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
